import { DateTime } from 'luxon';
import { useEffect, useRef } from 'react';
import { daylightSavingsChange, daylightSavingsChangeDown } from 'src/utils/date-utils';

export function Elog({
  lines,
  eventDate,
  timezone,
}: {
  lines: GraphLine[];
  eventDate: string;
  timezone: string;
}) {
  const canvasRef = useRef<null | HTMLCanvasElement>(null);

  useEffect(() => {
    const c = canvasRef.current!;
    const ctx = c.getContext('2d');

    renderToCanvas(c, ctx!, lines, eventDate, timezone);
  }, [lines, eventDate, timezone]);

  return (
    <canvas
      ref={canvasRef}
      id="log"
      width="1640"
      height="260"
      style={{ display: 'block', width: '820px', height: '130px' }}
    />
  );
}

export type GraphLine = {
  state: 'OFF' | 'SB' | 'D' | 'ON' | 'YM' | 'PU';
  start: string;
  startFull: string;
};

function stateToFlatState(
  state: 'OFF' | 'SB' | 'D' | 'ON' | 'YM' | 'PU' | ''
): 'OFF' | 'SB' | 'D' | 'ON' | '' {
  if (state === 'OFF' || state === 'PU') {
    return 'OFF';
  }
  if (state === 'SB') {
    return 'SB';
  }
  if (state === 'D') {
    return 'D';
  }
  if (state === 'ON' || state === 'YM') {
    return 'ON';
  }
  return '';
}
const topMarkers = [] as { h: string; offset: number }[];
for (let i = 0; i < 25; i += 1) {
  let h = `${i % 12}`;
  let offset = h.length * 5;
  if (i === 0 || i === 24) {
    h = 'M';
    offset = 6;
  } else if (i === 12) {
    h = 'N';
    offset = 6;
  }
  h = `${h}`;
  topMarkers.push({ h, offset });
}

const topMarkersDaylightSavings = [] as { h: string; offset: number }[];
for (let i = 0; i < 26; i += 1) {
  let val = i;
  if (i > 1) {
    val -= 1;
  }

  let h = `${val % 12}`;
  let offset = h.length * 5;
  if (val === 0 || val === 24) {
    h = 'M';
    offset = 6;
  } else if (val === 12) {
    h = 'N';
    offset = 6;
  } else if (i === 1) {
    h = '1dst';
    offset = 12;
  }
  h = `${h}`;
  topMarkersDaylightSavings.push({ h, offset });
}

const topMarkersDaylightSavingsDown = [] as { h: string; offset: number }[];
for (let i = 0; i < 24; i += 1) {
  let val = i;
  if (i > 1) {
    val += 1;
  }

  let h = `${val % 12}`;
  let offset = h.length * 5;
  if (val === 0 || val === 24) {
    h = 'M';
    offset = 6;
  } else if (val === 12) {
    h = 'N';
    offset = 6;
  } else if (i === 2) {
    h = '3dst';
    offset = 12;
  }
  h = `${h}`;
  topMarkersDaylightSavingsDown.push({ h, offset });
}

function renderToCanvas(
  c: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  data: GraphLine[],
  eventDate: string,
  timezone: string
) {
  const daylightSavingsChangeTime = daylightSavingsChange.get(eventDate || '');
  const daylightSavingsChangeTimeDown = daylightSavingsChangeDown.get(eventDate || '');

  const total = {
    OFF: 0,
    SB: 0,
    D: 0,
    ON: 0,
    '': 0,
  };
  let prevState: 'OFF' | 'SB' | 'D' | 'ON' | 'YM' | 'PU' | '' = '';
  const startOfDayDateTime = DateTime.fromISO(`${eventDate}T00:00:00`, { zone: timezone });
  const startOfDay = startOfDayDateTime.toISO()!;

  let prevTime = startOfDay;
  for (let i = 0; i < data.length; i += 1) {
    const current = data[i];
    const totalPrev = DateTime.fromISO(current.startFull)
      .diff(DateTime.fromISO(prevTime), 'seconds')
      .toObject().seconds!;
    total[stateToFlatState(prevState)] += totalPrev;

    prevTime = current.startFull;
    prevState = current.state;
  }

  const { width } = c;
  const height = c.height - 1;
  const startPoint = height / 7;

  const cubeWidth =
    // eslint-disable-next-line no-nested-ternary
    width / (daylightSavingsChangeTime ? 27 : daylightSavingsChangeTimeDown ? 25 : 26);
  const cubeHeight = (height - startPoint) / 4;

  const chartStartX = cubeWidth;
  const chartEndX = width - cubeWidth;
  const chartStartY = startPoint;
  const chartEndY = height;

  ctx.clearRect(0, 0, c.width, c.height);

  for (let i = cubeWidth; i < width - 1; i += cubeWidth) {
    ctx.beginPath();
    ctx.moveTo(i, startPoint);
    ctx.lineTo(i, height);
    ctx.stroke();

    if (i < width - cubeWidth - 1) {
      for (let m = 0; m < 4; m += 1) {
        ctx.beginPath();
        ctx.moveTo(i + cubeWidth / 2, startPoint + cubeHeight * m + cubeHeight / 2);
        ctx.lineTo(i + cubeWidth / 2, startPoint + cubeHeight * m + cubeHeight);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(i + cubeWidth / 4, startPoint + cubeHeight * m + (cubeHeight / 4) * 3);
        ctx.lineTo(i + cubeWidth / 4, startPoint + cubeHeight * m + cubeHeight);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(i + (cubeWidth / 4) * 3, startPoint + cubeHeight * m + (cubeHeight / 4) * 3);
        ctx.lineTo(i + (cubeWidth / 4) * 3, startPoint + cubeHeight * m + cubeHeight);
        ctx.stroke();
      }
    }
  }
  for (let i = 0; i < 5; i += 1) {
    const y = startPoint + cubeHeight * i;
    ctx.beginPath();
    ctx.moveTo(cubeWidth, y);
    ctx.lineTo(width - cubeWidth, y);
    ctx.stroke();
  }
  // eslint-disable-next-line no-nested-ternary
  const fullDaySeconds = daylightSavingsChangeTime
    ? toSeconds('25:00:00')
    : daylightSavingsChangeTimeDown
      ? toSeconds('23:00:00')
      : toSeconds('24:00:00');

  for (let i = 1; i < data.length; i += 1) {
    const startAsPcnt =
      DateTime.fromISO(data[i - 1].startFull)
        .diff(startOfDayDateTime, 'seconds')
        .toObject().seconds! / fullDaySeconds;
    const endAsPcnt =
      DateTime.fromISO(data[i].startFull).diff(startOfDayDateTime, 'seconds').toObject().seconds! /
      fullDaySeconds;

    const start = chartStartX + startAsPcnt * (chartEndX - chartStartX);
    const end = chartStartX + endAsPcnt * (chartEndX - chartStartX); // pcnt * width
    const previousY = getY(data[i - 1], chartStartY, cubeHeight);
    const y = getY(data[i], chartStartY, cubeHeight);

    const prelineWidth = ctx.lineWidth;
    const prestrokeStyle = ctx.strokeStyle;
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#004080';

    // this is the horizontal line for the previous data item
    ctx.beginPath();
    if (data[i - 1].state === 'PU' || data[i - 1].state === 'YM') {
      ctx.setLineDash([15, 10]);
    }
    ctx.moveTo(start, previousY);
    ctx.lineTo(end, previousY);
    ctx.stroke();
    ctx.setLineDash([]);

    // we want to also connect lines
    if (previousY !== -1) {
      ctx.beginPath();
      ctx.moveTo(end, previousY);
      ctx.lineTo(end, y);
      ctx.stroke();
    }

    ctx.lineWidth = prelineWidth;
    ctx.strokeStyle = prestrokeStyle;
  }

  ctx.font = '18px helvetica';
  const pullup = 5;
  ctx.fillText('OFF', 15, 85);
  ctx.fillText('SB', 27, 140);
  ctx.fillText('D', 38, 195);
  ctx.fillText('ON', 24, 250);

  // eslint-disable-next-line no-nested-ternary
  const markers = daylightSavingsChangeTime
    ? topMarkersDaylightSavings
    : daylightSavingsChangeTimeDown
      ? topMarkersDaylightSavingsDown
      : topMarkers;

  for (let i = 0; i < markers.length; i += 1) {
    ctx.fillText(markers[i].h, cubeWidth + cubeWidth * i - markers[i].offset, 26);
  }

  ctx.fillText(formatTimeForGraph(total.OFF), width - cubeWidth + 9, 85 - pullup);
  ctx.fillText(formatTimeForGraph(total.SB), width - cubeWidth + 9, 140 - pullup);
  ctx.fillText(formatTimeForGraph(total.D), width - cubeWidth + 9, 195 - pullup);
  ctx.fillText(formatTimeForGraph(total.ON), width - cubeWidth + 9, 250 - pullup);
}

function toSeconds(time: string): number {
  const parts = time.split(':');
  return Number(parts[2]) + 60 * Number(parts[1]) + 60 * 60 * Number(parts[0]);
}

function getY({ state }: GraphLine, chartStartY: number, cubeHeight: number) {
  if (state === 'OFF' || state === 'PU') {
    return chartStartY + cubeHeight / 2;
  }
  if (state === 'SB') {
    return chartStartY + cubeHeight + cubeHeight / 2;
  }
  if (state === 'D') {
    return chartStartY + 2 * cubeHeight + cubeHeight / 2;
  }
  if (state === 'ON' || state === 'YM') {
    return chartStartY + 3 * cubeHeight + cubeHeight / 2;
  }
  return 0;
}

function formatTimeForGraph(seconds: number): string {
  const formattedNumber = (seconds / 60 / 60).toFixed(2);

  // Split the number into integer and decimal parts
  const parts = formattedNumber.split('.');

  // If the integer part has only one digit, add a leading zero
  if (parts[0].length === 1) {
    parts[0] = `0${parts[0]}`;
  }

  // Return the formatted number
  return parts.join('.');
}
